/**
 * Normalize image URLs into a form that's safe to drop into <img src>.
 *
 * The trap this exists to close: Google Drive's "Copy link" gives a viewer
 * URL like `https://drive.google.com/file/d/<id>/view?usp=drive_link`. That
 * URL returns an HTML page (the Drive viewer) when fetched as `<img src>`,
 * so the image just silently fails to render — works when pasted in a
 * browser tab, broken in the report. We've hit this on K5's
 * vaikutuskartta; it'll happen again every time a fasilitaattori grabs a
 * URL straight from Drive.
 *
 * Normalisation converts every Drive URL variant to the lh3.googleusercontent
 * CDN form `https://lh3.googleusercontent.com/d/<id>`, which serves the
 * raw image bytes with appropriate content-type. Non-Drive URLs pass
 * through unchanged.
 *
 * Apply this anywhere a user-provided URL ends up in <img src> — see
 * AtmosphereGroup, GroupPhoto, ReportImageSlot, ReportWhatIf,
 * ImageLightbox. Cheap to call (regex), idempotent (CDN URLs pass
 * through).
 */

const FILE_VIEW = /^https?:\/\/(?:www\.)?drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i;
const OPEN_QUERY = /^https?:\/\/(?:www\.)?drive\.google\.com\/(?:open|uc|thumbnail)\?(?:[^#]*&)?id=([a-zA-Z0-9_-]+)/i;
const LH3 = /^https?:\/\/lh\d+\.googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/i;

export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();

  // Already a CDN URL → return as-is (idempotent).
  if (LH3.test(trimmed)) return trimmed;

  // drive.google.com/file/d/<id>/view... → CDN
  const m1 = trimmed.match(FILE_VIEW);
  if (m1) return `https://lh3.googleusercontent.com/d/${m1[1]}`;

  // drive.google.com/open?id=... | uc?id=... | thumbnail?id=... → CDN
  const m2 = trimmed.match(OPEN_QUERY);
  if (m2) return `https://lh3.googleusercontent.com/d/${m2[1]}`;

  return trimmed;
}
